import * as React from 'react';
import { t } from '@reos/i18n';
import { baseUrl } from '../lib/api.js';

/**
 * Krok GDPR1 bod 2 — Nastaveni -> „Ochrana údajů": vzorovy zaznam o
 * cinnostech zpracovani (cl. 30 odst. 1) pro proverovani vlastnika v ISIR.
 *
 * Zaznam generuje API pro kancelar PRIHLASENEHO uzivatele (zadne id v adrese,
 * `apps/api/src/routes/privacy-record.ts`); PDF jde pres Gotenberg. Kdyz
 * prevod do PDF neni k dispozici (503), karta to rekne a nabidne nahled
 * k tisku, ze ktereho jde PDF ulozit prohlizecem.
 */

const RECORD_PATH = '/v1/tenant-settings/privacy/isir-record';
const FILENAME = 'zaznam-cinnosti-zpracovani-isir.pdf';

type Status = 'idle' | 'busy' | 'pdf-unavailable' | 'failed';

function saveBlob(blob: Blob, filename: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function PrivacyRecordCard(): React.JSX.Element {
  const [status, setStatus] = React.useState<Status>('idle');

  async function download(): Promise<void> {
    setStatus('busy');
    try {
      const res = await fetch(`${baseUrl}${RECORD_PATH}.pdf`, { credentials: 'include' });
      if (res.status === 503) {
        setStatus('pdf-unavailable');
        return;
      }
      if (!res.ok) {
        setStatus('failed');
        return;
      }
      saveBlob(await res.blob(), FILENAME);
      setStatus('idle');
    } catch {
      setStatus('failed');
    }
  }

  async function preview(): Promise<void> {
    try {
      const res = await fetch(`${baseUrl}${RECORD_PATH}.html`, { credentials: 'include' });
      if (!res.ok) {
        setStatus('failed');
        return;
      }
      const blob = new Blob([await res.text()], { type: 'text/html' });
      if (typeof URL.createObjectURL === 'function') {
        window.open(URL.createObjectURL(blob), '_blank', 'noopener');
      }
    } catch {
      setStatus('failed');
    }
  }

  return (
    <section
      className="rh-card"
      style={{ maxWidth: 640, padding: 16, marginBottom: 16 }}
      data-testid="nastaveni-privacy-card"
      aria-labelledby="nastaveni-privacy-heading"
    >
      <h2 id="nastaveni-privacy-heading" className="mb-1 text-base font-semibold">
        {t('privacy.isirRecord.settingsHeading', { locale: 'cs' })}
      </h2>
      <p className="mb-1 text-sm font-medium">{t('privacy.isirRecord.activity', { locale: 'cs' })}</p>
      <p className="mb-3 text-sm text-gray-600" data-testid="nastaveni-privacy-what">
        {t('privacy.isirRecord.settingsWhat', { locale: 'cs' })}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="rh-btn rh-btn--primary"
          onClick={() => void download()}
          disabled={status === 'busy'}
          data-testid="nastaveni-privacy-download"
        >
          {t('privacy.isirRecord.download', { locale: 'cs' })}
        </button>
        <button
          type="button"
          className="rh-btn"
          onClick={() => void preview()}
          data-testid="nastaveni-privacy-preview"
        >
          {t('privacy.isirRecord.preview', { locale: 'cs' })}
        </button>
      </div>
      {status === 'pdf-unavailable' && (
        <p role="status" className="mt-2 text-sm" data-testid="nastaveni-privacy-pdf-unavailable">
          {t('privacy.isirRecord.pdfUnavailable', { locale: 'cs' })}
        </p>
      )}
      {status === 'failed' && (
        <p role="alert" className="mt-2 text-sm text-red-700" data-testid="nastaveni-privacy-failed">
          {t('privacy.isirRecord.downloadFailed', { locale: 'cs' })}
        </p>
      )}
    </section>
  );
}
